<ng-template #selectContainer>
  <ng-container *ngIf="props.options !== null">
    <ng-container *ngIf="hasAsyncItems; else hasSyncItems">
      <ng-container
        [ngTemplateOutlet]="select"
        [ngTemplateOutletContext]="{ options: props.options | async }"
      ></ng-container>
    </ng-container>

    <ng-template #hasSyncItems>
      <ng-container [ngTemplateOutlet]="select" [ngTemplateOutletContext]="{ options: props.options }"></ng-container>
    </ng-template>
  </ng-container>
</ng-template>

<ng-template #select let-options="options">
  <div class="position-relative">
    <ng-select
      #ngSelect
      [appendTo]="props.appendTo"
      [attr.id]="field.id"
      [bindLabel]="props.bindLabel || 'label'"
      [bindValue]="props.bindValue || 'value'"
      [class.enable-fullscreen]="props.enableFullscreen"
      [class.fullscreen]="fullscreen"
      [class.ng-select-striped]="!!props.striped"
      [class.show-arrow]="props.showArrow !== false"
      [class.too-many-options]="hasAsyncItems && (props.options | async)?.length > TOO_MANY_OPTIONS"
      [clearable]="props.clearable || false"
      [clearSearchOnAdd]="true"
      [closeOnSelect]="props.closeOnSelect !== undefined ? props.closeOnSelect : !props.multiple"
      [dropdownPosition]="this.fullscreen ? 'bottom' : 'auto'"
      [formControl]="formControl"
      [hideSelected]="props.multiple"
      [isOpen]="props.isOpen"
      [items]="options"
      [loading]="loading"
      [multiple]="props.multiple"
      [notFoundText]="notFoundText"
      [placeholder]="fullscreen ? '' : placeholder"
      [readonly]="props.readonly"
      [searchable]="props.searchable !== undefined ? props.searchable : true"
      [typeahead]="hasAsyncItems && props.onSearch ? input$ : null"
      [typeToSearchText]="fullscreen ? '' : placeholder"
      (change)="onChange($event)"
      (click)="!formControl.disabled && goFullscreen($event, null)"
      (search)="!formControl.disabled && goFullscreen(null, $event)"
      class="{{ props.classNames }} flex-grow-1"
      groupBy="group"
    >
      <ng-template let-item="item" ng-optgroup-tmp>
        {{ item.group }}
      </ng-template>

      <ng-template let-clear="clear" let-item="item" ng-label-tmp>
        <ng-container *ngIf="!props.labelTemplate">{{ item.label }}</ng-container>
        <ng-container
          *ngIf="props.labelTemplate"
          [ngTemplateOutlet]="props.labelTemplate"
          [ngTemplateOutletContext]="{ item: item }"
        ></ng-container>
        <span
          (click)="clear(item)"
          class="ng-value-icon right"
          aria-hidden="true"
          astrobinEventPreventDefault
          astrobinEventStopPropagation
          >×</span
        >
      </ng-template>

      <ng-template let-item="item" ng-option-tmp>
        <ng-container *ngIf="!props.optionTemplate">
          <div class="title">{{ item.label }}</div>
          <div *ngIf="item.description" class="description">{{ item.description }}</div>
        </ng-container>
        <ng-container
          *ngIf="props.optionTemplate"
          [ngTemplateOutlet]="props.optionTemplate"
          [ngTemplateOutletContext]="{ item }"
        ></ng-container>
      </ng-template>

      <ng-template ng-header-tmp>
        <ng-container *ngIf="hasAsyncItems">
          <ng-container *ngIf="props.options | async as options">
            <div *ngIf="value?.length > 0 && options?.length > TOO_MANY_OPTIONS" class="too-many-options-label">
              <fa-icon icon="exclamation-triangle"></fa-icon>
              {{ "Type more to refine your search." | translate }}
            </div>
          </ng-container>
        </ng-container>
      </ng-template>

      <ng-template ng-footer-tmp>
        <div *ngIf="!!value && !!props.addTag && showCreateNewButton; else footerTemplateExtra" class="add-tag">
          <div [innerHTML]="'Can\'t find <strong>{{0}}</strong>?' | translate: { '0': value }" class="cant-find"></div>

          <ng-container [ngTemplateOutlet]="footerTemplateExtra"></ng-container>

          <button
            *ngIf="!!value && !!props.addTag && showCreateNewButton"
            [class.loading]="loading"
            [disabled]="formControl.disabled"
            (click)="onAddTag(value)"
            class="btn btn-outline-primary"
          >
            {{ "Create new" }}
          </button>
        </div>
      </ng-template>

      <ng-template #footerTemplateExtra>
        <ng-container *ngIf="props.footerTemplateExtra">
          <div class="footer-template-extra">
            <ng-template [ngTemplateOutlet]="props.footerTemplateExtra"></ng-template>
          </div>
        </ng-container>
      </ng-template>
    </ng-select>

    <a
      *ngIf="!fullscreen && props.enableFullscreen && cypressTesting"
      [class.disabled]="formControl.disabled"
      [ngbTooltip]="'Disable fullscreen mode' | translate"
      (click)="!formControl.disabled && toggleEnableFullscreen()"
      class="toggle-enable-fullscreen"
      astrobinEventPreventDefault
      astrobinEventStopPropagation
      href="#"
    >
      <fa-icon [class.text-muted]="!props.enableFullscreen" icon="expand"></fa-icon>
    </a>
  </div>
</ng-template>

<ng-template #ngSelectModal>
  <div class="modal-header">
    <h4 id="ng-select-modal" class="modal-title">
      <ng-container *ngIf="field.props.fullScreenLabel; else noFullScreenLabel">
        {{ field.props.fullScreenLabel }}
      </ng-container>
      <ng-template #noFullScreenLabel>
        {{ field.props.label }}
      </ng-template>
    </h4>
    <button (click)="exitFullscreen()" class="close" aria-describedby="modal-title" type="button">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">
    <div class="ng-select-container">
      <label [attr.for]="field.id">
        {{ placeholder }}
      </label>

      <div class="d-flex">
        <div class="flex-grow-1">
          <ng-container [ngTemplateOutlet]="selectContainer"></ng-container>
        </div>
        <button *ngIf="props.multiple" (click)="exitFullscreen()" class="btn btn-primary btn-sm btn-no-block">
          {{ "Done" | translate }}
        </button>
      </div>
    </div>

    <div *ngIf="!!props.fullScreenBodyTemplate" class="full-screen-body-template-container mt-3">
      <ng-container [ngTemplateOutlet]="props.fullScreenBodyTemplate"></ng-container>
    </div>
  </div>

  <div class="modal-footer">
    <button (click)="exitFullscreen()" class="btn btn-secondary" type="button">
      {{ "Close" | translate }}
    </button>
  </div>
</ng-template>

<ng-container [ngTemplateOutlet]="selectContainer"></ng-container>
